import React from "react";
import LegalPageLayout from "./LegalPageLayout";

const TrainerTermsAndConditions: React.FC = () => {
  return (
    <LegalPageLayout title="Pheefee Trainer Terms and Conditions" lastUpdated="April 17, 2026">
      <p>
        These Trainer Terms and Conditions, together with any policies, guidelines, payment terms,
        privacy-related disclosures, and other documents expressly incorporated by reference, form
        a legally binding agreement between Pheefee Technologies Inc. (&ldquo;Pheefee,&rdquo;
        &ldquo;Company,&rdquo; &ldquo;we,&rdquo; &ldquo;us,&rdquo; or &ldquo;our&rdquo;) and the
        individual or entity accepting these Terms (&ldquo;Trainer,&rdquo; &ldquo;you,&rdquo; or
        &ldquo;your&rdquo;).
      </p>
      <p>
        By applying to join, accessing, using, or continuing to use the Pheefee platform, or by
        clicking to accept these Terms, you agree to be bound by them. If you do not agree, you may
        not apply for, access, or use the platform.
      </p>

      <h2>1. Platform Purpose and Contractual Model</h2>
      <p>
        <strong>1.1 Marketplace Role.</strong> Pheefee operates an online platform that enables
        dog owners and other pet-related users (&ldquo;Clients&rdquo;) to locate, connect with, and
        receive on demand, remote dog-training-related guidance and services from independent
        trainers.
      </p>
      <p>
        <strong>1.2 No Training Services Provided by Pheefee.</strong> Pheefee does not provide
        dog training services, veterinary services, medical services, behavioral health treatment,
        emergency services, or any other end-user professional services. Pheefee makes available a
        communications, booking, payment, moderation, and administrative platform only.
      </p>
      <p>
        <strong>1.3 Separate Relationship Between Trainer and Client.</strong> Any services you
        provide to a Client are provided solely by you, in your individual capacity or through your
        business entity, and not by Pheefee. You alone are responsible for your communications with
        Clients, your training advice, your conduct, your service quality, your compliance with
        law, and all consequences arising from the services you provide.
      </p>
      <p>
        <strong>1.4 Platform Activity and Oversight.</strong> Pheefee may actively manage, monitor,
        review, record, transcribe, summarize, moderate, investigate, suspend, or remove platform
        activity in order to protect users, maintain quality standards, enforce these Terms,
        investigate complaints, improve platform operations, and protect the reputation and
        business interests of Pheefee. Such actions do not create an employment, agency,
        partnership, joint venture, or supervisory relationship between you and Pheefee.
      </p>

      <h2>2. Eligibility and Application</h2>
      <p>
        <strong>2.1 Eligibility.</strong> To apply for and use the platform, you represent and
        warrant that:
      </p>
      <ul>
        <li>you are at least eighteen (18) years old;</li>
        <li>
          you have the full right, power, and authority to enter into and perform under these
          Terms;
        </li>
        <li>
          you are legally permitted to work and provide services in each jurisdiction in which you
          operate;
        </li>
        <li>you will use the platform for lawful business purposes only;</li>
        <li>
          all information you submit to Pheefee is truthful, current, complete, and not misleading;
          and
        </li>
        <li>
          you have the experience, skill, judgment, and qualifications necessary to provide the
          services you offer.
        </li>
      </ul>
      <p>
        <strong>2.2 Application Fee.</strong> As part of the onboarding and review process, Pheefee
        may require payment of a non-refundable application fee. Unless Pheefee expressly states
        otherwise in writing, the application fee is $50.00 and becomes fully non-refundable once
        submitted, regardless of whether you pass screening, are accepted, later withdraw, or are
        denied access to the platform. The parties acknowledge that the application process
        includes administrative review and background screening costs.
      </p>
      <p>
        <strong>2.3 Background Checks.</strong> Pheefee may require identity verification,
        credential review, and criminal or other background checks as part of onboarding and at any
        time thereafter. Background screening may be performed by a third-party provider, including
        Checkr or another provider selected by Pheefee. Any misdemeanor or felony conviction, any
        animal-related offense, or any other issue Pheefee determines, in its sole discretion, to
        present a safety, trust, legal, or reputational risk may result in rejection, suspension,
        or removal from the platform.
      </p>
      <p>
        <strong>2.4 Continuing Disclosure Duty.</strong> You must promptly disclose to Pheefee any
        fact or development that could affect your eligibility, including any pending criminal
        charge, conviction, complaint, insurance lapse, suspension, regulatory inquiry, or
        allegation involving dishonesty, violence, abuse, animal welfare, or professional
        misconduct.
      </p>

      <h2>3. Independent Contractor Status</h2>
      <p>
        <strong>3.1 Independent Business.</strong> You acknowledge and agree that you are an
        independent contractor and independent business owner, and not an employee, agent, partner,
        joint venturer, franchisee, or representative of Pheefee.
      </p>
      <p>
        <strong>3.2 No Employment Rights.</strong> Nothing in these Terms creates any entitlement
        to wages, salary, overtime, benefits, workers&rsquo; compensation, unemployment insurance,
        retirement benefits, tax withholding, or other employee benefits from Pheefee.
      </p>
      <p>
        <strong>3.3 Trainer Control of Business Decisions.</strong> Subject to these Terms and the
        platform&rsquo;s operational rules, you control whether to make yourself available on the
        platform, what services you offer within approved categories, and what pricing you set,
        provided that you comply with platform minimum pricing requirements. Trainers may toggle
        availability on and off and are not assigned fixed schedules by Pheefee.
      </p>
      <p>
        <strong>3.4 No Restriction on Independent Business Activity.</strong> You may provide
        services outside the platform and may maintain your own independent clientele, subject to
        your obligations under these Terms regarding Pheefee-introduced Clients, confidentiality,
        platform integrity, and prohibited conduct.
      </p>
      <p>
        <strong>3.5 Taxes and Legal Compliance.</strong> You are solely responsible for all taxes,
        filings, reporting obligations, permits, registrations, and licenses arising from your
        business and your use of the platform, including all local, state, and federal tax
        obligations.
      </p>

      <h2>4. Trainer Account and Profile</h2>
      <p>
        <strong>4.1 Account Registration.</strong> You must create and maintain an account and
        provide complete and accurate account information. You may not create more than one account
        for the same individual or business without Pheefee&rsquo;s written consent.
      </p>
      <p>
        <strong>4.2 Credentials and Profile Claims.</strong> You may include your background,
        credentials, certifications, specialties, experience, biography, resume, and similar
        information in your profile, but you remain solely responsible for the truth, accuracy,
        completeness, and current status of every representation you make. Pheefee may review or
        verify profile content during onboarding or later, but has no duty to independently verify
        every statement you submit. Any false, misleading, exaggerated, unsupported, or outdated
        claim may result in rejection, suspension, removal, or other action by Pheefee.
      </p>
      <p>
        <strong>4.3 Login Credentials.</strong> You are responsible for maintaining the
        confidentiality of your account credentials and for all activity that occurs under your
        account.
      </p>

      <h2>5. Scope of Permitted Services</h2>
      <p>
        <strong>5.1 Permitted Services.</strong> You may use the platform only to provide remote
        dog-training-related guidance, consultation, and similar services that Pheefee permits from
        time to time.
      </p>
      <p>
        <strong>5.2 Prohibited Services.</strong> You may not use the platform to provide or
        purport to provide:
      </p>
      <ul>
        <li>in-person training services through the current version of the platform;</li>
        <li>emergency advice or crisis-response services;</li>
        <li>
          veterinary, medical, diagnostic, prescription, rehabilitation, or mental health services;
        </li>
        <li>any service that requires a professional license you do not hold;</li>
        <li>any illegal, unsafe, abusive, fraudulent, or misleading service; or</li>
        <li>any service outside the categories or boundaries approved by Pheefee.</li>
      </ul>
      <p>
        <strong>5.3 Emergency Situations.</strong> You must not present the platform as suitable
        for emergencies. If a Client presents an emergency, urgent medical concern, or safety issue
        outside ordinary training guidance, you must direct the Client to an appropriate
        veterinarian, emergency provider, or emergency services, as applicable.
      </p>
      <p>
        <strong>5.4 Jurisdictional Compliance.</strong> You are solely responsible for determining
        whether your provision of services through the platform is lawful in each jurisdiction in
        which you offer, market, advertise, or provide such services, including any jurisdiction in
        which you are located, licensed, certified, registered, or in which a Client is located.
        You shall remain current with, and comply at all times with, all applicable federal, state,
        local, and other laws, regulations, licensing requirements, certification requirements,
        registration requirements, and professional or industry standards applicable to your
        services. You shall not offer, market, or provide services through the platform in any
        jurisdiction where you do not meet such requirements.
      </p>

      <h2>6. Professional Conduct and Trainer Standards</h2>
      <p>
        <strong>6.1 General Standard.</strong> You must conduct yourself professionally, ethically,
        and lawfully at all times in connection with the platform and all Client interactions.
      </p>
      <p>
        <strong>6.2 Specific Conduct Rules.</strong> You shall not:
      </p>
      <ul>
        <li>
          engage in lewd, sexually explicit, harassing, threatening, abusive, discriminatory, or
          offensive conduct;
        </li>
        <li>
          appear impaired or under the influence of alcohol, illegal drugs, or any substance that
          could affect your judgment or professionalism while using the platform;
        </li>
        <li>mistreat, encourage the mistreatment of, or endorse cruelty toward any animal;</li>
        <li>
          misrepresent your qualifications, results, services, methods, affiliations, or identity;
        </li>
        <li>
          use the platform for spam, solicitation unrelated to permitted services, fraud, or
          deceptive practices;
        </li>
        <li>
          pressure Clients to move communications or transactions outside the platform in violation
          of these Terms;
        </li>
        <li>violate any applicable law, regulation, court order, or third-party right; or</li>
        <li>
          act in any manner that Pheefee determines, in its sole discretion, threatens user safety,
          platform trust, legal compliance, or Pheefee&rsquo;s reputation.
        </li>
      </ul>
      <p>
        <strong>6.3 Service Standard.</strong> You agree to provide services carefully,
        competently, and in a manner consistent with reasonable industry standards for remote
        dog-training guidance. You shall offer only services for which you have the necessary
        knowledge, experience, and ability.
      </p>

      <h2>7. Insurance</h2>
      <p>
        <strong>7.1 Coverage Requirement.</strong> Before providing any services through the
        platform, you must maintain insurance that covers your online instruction and remote
        professional activities, with minimum liability coverage of $1,000,000.00 per occurrence or
        such greater amount as Pheefee may require in writing. Pheefee Technologies Inc. must be
        named as an additional insured where available and appropriate to the policy structure.
      </p>
      <p>
        <strong>7.2 Proof of Insurance.</strong> You must provide proof of insurance in a form
        acceptable to Pheefee before activation and at any renewal or other time upon request.
      </p>
      <p>
        <strong>7.3 Lapse or Cancellation.</strong> You must notify Pheefee immediately of any
        cancellation, lapse, non-renewal, material change, or reservation of rights affecting your
        coverage. Pheefee may suspend or terminate your account immediately if your required
        coverage lapses or becomes inadequate.
      </p>

      <h2>8. Sessions, Communications, Recordings, and Summaries</h2>
      <p>
        <strong>8.1 Platform Communications.</strong> You agree to use the tools, systems, and
        communication channels designated by Pheefee for services arranged through the platform.
      </p>
      <p>
        <strong>8.2 Monitoring, Recording, and Transcription.</strong> Pheefee may monitor, record,
        transcribe, store, review, audit, and use platform communications, video sessions,
        messages, summaries, and related records for quality assurance, complaint review, trust and
        safety, training, policy enforcement, legal compliance, dispute resolution, product
        improvement, and other legitimate business purposes. By using the platform, Trainer
        consents to such monitoring, recording, and transcription by Pheefee and acknowledges that
        Pheefee may require corresponding consent from Clients and other participants as a
        condition of using the platform. Trainers may not independently record, download,
        screen-record, save, copy, or otherwise create their own audio or video recordings of
        sessions or platform communications without Pheefee&rsquo;s prior written consent. Pheefee
        may, but is not obligated to, provide Trainer access to platform-held recordings, subject
        to availability, policy, technical limitations, and applicable law.
      </p>
      <p>
        <strong>8.3 Trainer Cooperation.</strong> You agree to cooperate promptly with any platform
        investigation, complaint review, audit, or compliance inquiry.
      </p>
      <p>
        <strong>8.4 Summary Review Obligation.</strong> For any session for which Pheefee generates
        or requires a session summary, you must review, edit if necessary, and approve the summary
        within two (2) hours after the session or within any shorter or longer period designated by
        Pheefee.
      </p>
      <p>
        <strong>8.5 Failure to Timely Review.</strong> If you do not review or edit the summary
        within the required time:
      </p>
      <ul>
        <li>the summary may be deemed automatically approved;</li>
        <li>Pheefee may finalize, publish, store, or use the summary in its discretion; and</li>
        <li>
          repeated failures may constitute a policy violation and may result in payout delay,
          suspension, or removal from the platform.
        </li>
      </ul>

      <h2>9. Pricing, Availability, and Session Performance</h2>
      <p>
        <strong>9.1 Trainer Pricing.</strong> You may set your own pricing through the platform,
        subject to any minimums, maximums, format rules, promotional structures, or pricing logic
        Pheefee adopts from time to time. At the time of these Terms, the minimum pricing model
        includes $10.00 for the first ten (10) minutes and $1.00 for each additional minute, unless
        Pheefee updates that structure.
      </p>
      <p>
        <strong>9.2 Availability.</strong> You are not required to maintain a fixed schedule. You
        may make yourself available by toggling on or otherwise indicating your availability
        through the platform.
      </p>
      <p>
        <strong>9.3 Session Duration.</strong> When a Client prepays for a specified session
        length, you are responsible for being present, responsive, and available for the full
        purchased time, except where a shorter session results from Client choice, safety concerns,
        technical failure outside your control, or another reason accepted by Pheefee.
      </p>

      <h2>10. Fees, Payouts, Refunds, and Chargebacks</h2>
      <p>
        <strong>10.1 Payment Processor.</strong> Pheefee may use Stripe Connect or any other
        payment processor selected by Pheefee. You must establish and maintain any required payment
        account and provide any information necessary to receive payouts.
      </p>
      <p>
        <strong>10.2 Trainer Share.</strong> Unless Pheefee states otherwise in writing, trainer
        compensation shall follow a 70/30 split, with seventy percent (70%) allocated to the
        Trainer and thirty percent (30%) retained by Pheefee. The split shall apply to net amounts
        actually collected from the Client for the applicable session or service, after deduction
        of refunds, credits, reversals, chargebacks, payment processor fees, transaction fees,
        sales taxes or similar amounts collected and remitted, and any other third-party fees or
        government-imposed amounts tied to the transaction. Pheefee may revise pricing models,
        commission structures, beta terms, or pilot economics upon notice.
      </p>
      <p>
        Notwithstanding the foregoing, certain Trainers may be offered separate beta launch
        compensation, participation requirements, and incentive terms pursuant to a written Beta
        Trainer Addendum or other written notice from Pheefee.
      </p>
      <p>
        <strong>10.3 Payout Timing.</strong> Payout timing may depend on the payment processor, the
        Trainer&rsquo;s selected payout settings, reserve policies, fraud review, dispute status,
        and other operational considerations. Pheefee may delay or withhold payouts as reasonably
        necessary to investigate complaints, suspected fraud, policy violations, chargebacks, or
        other issues.
      </p>
      <p>
        <strong>10.4 Refund Authority.</strong> Pheefee shall have primary discretion to review
        complaints and determine whether to issue a full refund, partial refund, account credit,
        service credit, or no refund at all. In exercising that discretion, Pheefee may consider
        the Trainer&rsquo;s response, session records, summaries, transcripts, prior complaints,
        policy violations, Client safety concerns, and any other information Pheefee deems
        relevant.
      </p>
      <p>
        <strong>10.5 Refunds and Offsets.</strong> Pheefee may deduct or offset refunds, credits,
        chargebacks, reversals, processor fees, dispute costs, and related losses from current or
        future payouts otherwise owed to you.
      </p>
      <p>
        <strong>10.6 Annual Courtesy Absorption.</strong> Pheefee may, in its discretion, absorb
        one refund per Trainer per calendar year without charge-back to the Trainer, but Pheefee
        has no obligation to do so. Additional complaints, refunds, or chargebacks may result in
        offsets, corrective action, suspension, or removal, as determined by Pheefee.
      </p>
      <p>
        <strong>10.7 No Guaranteed Earnings.</strong> Pheefee does not guarantee any minimum number
        of Clients, sessions, leads, bookings, revenue, or earnings.
      </p>

      <h2>11. Soft Off-Platform Restrictions</h2>
      <p>
        <strong>11.1 Platform Integrity.</strong> Pheefee invests substantial resources in
        attracting, screening, supporting, and maintaining Clients and Trainers. You agree not to
        use the platform in a manner that undermines the platform&rsquo;s legitimate business
        interests.
      </p>
      <p>
        <strong>11.2 Restricted Clients.</strong> For purposes of this Section, a
        &ldquo;Pheefee-Introduced Client&rdquo; means any Client first introduced to you through
        the platform or any Client with whom you first communicated, booked, or provided services
        through the platform.
      </p>
      <p>
        <strong>11.3 Restricted Period.</strong> During your use of the platform and for a period
        of two (2) years following the later of (a) the date of first introduction to a
        Pheefee-Introduced Client, or (b) the date of the last session, communication, booking, or
        transaction involving that Pheefee-Introduced Client through the platform, you shall not
        knowingly solicit, encourage, divert, or accept such Client off-platform for the purpose of
        avoiding platform processes, fees, policies, monitoring, or protections.
      </p>
      <p>
        <strong>11.4 Monitoring and Enforcement.</strong> Pheefee may monitor communications and
        activity for compliance with this Section. A violation may result in warning, removal of
        access, suspension, termination, forfeiture or withholding of payouts otherwise permitted
        by law, or other action reasonably necessary to protect the platform.
      </p>
      <p>
        <strong>11.5 No General Exclusivity.</strong> This Section does not prohibit you from
        operating your independent business generally, accepting non-Pheefee clients, or using
        other platforms, provided you do not misuse Pheefee relationships or platform access to
        divert Pheefee-introduced Clients.
      </p>

      <h2>12. Trainer Representations and Warranties</h2>
      <p>You represent, warrant, and covenant that:</p>
      <ul>
        <li>
          all information you provide to Pheefee and Clients is truthful, complete, and not
          misleading;
        </li>
        <li>you will only provide services you are qualified to provide;</li>
        <li>you will comply with all applicable laws, regulations, and industry requirements;</li>
        <li>
          you hold and will maintain all necessary licenses, permits, approvals, registrations, and
          insurance;
        </li>
        <li>you will not present yourself as an employee, agent, or representative of Pheefee;</li>
        <li>
          you will keep Client information secure and use it only as permitted by these Terms and
          applicable law; and
        </li>
        <li>
          you will promptly notify Pheefee if any statement in this Section becomes inaccurate.
        </li>
      </ul>

      <h2>13. Intellectual Property; Platform Content; Trainer Content</h2>
      <p>
        <strong>13.1 Pheefee Property.</strong> The platform, including its software, workflows,
        interface, trademarks, trade names, service marks, branding, compilations, designs,
        functionality, summaries, recordings, transcripts, documentation, and all related
        intellectual property rights, belongs exclusively to Pheefee or its licensors.
      </p>
      <p>
        <strong>13.2 Ownership of Platform-Generated and Service-Related Content.</strong> As
        between you and Pheefee, Pheefee shall own all rights, title, and interest in and to all
        summaries, transcripts, session recordings, moderation records, complaint files, analytics,
        platform-generated materials, and all content created, captured, generated, or compiled
        through or for the operation of the platform, whether created automatically, by Pheefee
        personnel, or in connection with services provided through the platform.
      </p>
      <p>
        <strong>13.3 License to Trainer Materials.</strong> To the extent you upload, submit,
        display, or otherwise provide biographies, profile content, credentials, videos, images,
        text, training materials, communications, or other content to or through the platform, you
        grant Pheefee a worldwide, perpetual, irrevocable, sublicensable, transferable,
        royalty-free license to host, use, reproduce, store, modify, adapt, distribute, display,
        publish, review, create derivative works from, and otherwise exploit such content for
        platform operations, legal compliance, marketing, quality assurance, training, trust and
        safety, and business purposes.
      </p>
      <p>
        <strong>13.4 Pre-Existing Trainer Materials.</strong> If you provide any materials you
        owned before uploading them to the platform, you retain ownership of those pre-existing
        materials, subject to the license granted above. You represent that you have all rights
        needed to grant that license.
      </p>

      <h2>14. Privacy, Data Use, and Confidentiality</h2>
      <p>
        <strong>14.1 Client Information.</strong> You may receive personal information relating to
        Clients. You shall use such information only as necessary to provide services through the
        platform, communicate about permitted services, comply with law, or otherwise as authorized
        by Pheefee and the Client.
      </p>
      <p>
        <strong>14.2 Legal Compliance.</strong> You must comply with all applicable privacy, data
        security, marketing, consumer protection, telemarketing, and communications laws in your
        collection, use, storage, and disclosure of personal information.
      </p>
      <p>
        <strong>14.3 Confidential Information.</strong> You shall keep confidential all non-public
        information relating to Pheefee, the platform, Clients, other trainers, platform
        operations, complaints, investigations, pricing structures, product features, business
        plans, and any other information that a reasonable person would understand to be
        confidential.
      </p>
      <p>
        <strong>14.4 Exclusions.</strong> Confidentiality obligations do not apply to information
        that becomes public through no fault of the receiving party, was already lawfully known
        without restriction, is independently developed without use of confidential information, or
        must be disclosed by law.
      </p>
      <p>
        <strong>14.5 Social Media and Public Statements.</strong> You may state truthfully that you
        use or participate in the Pheefee platform. You shall not: (a) disclose any confidential,
        proprietary, private, or Client-specific information; (b) post false, misleading,
        harassing, threatening, or malicious statements about Pheefee, its personnel, Clients, or
        other trainers; (c) imply that Pheefee endorses you or your services except as expressly
        authorized in writing; or (d) use Pheefee&rsquo;s name, trademarks, branding, or platform
        affiliation in a manner that violates these Terms or applicable law.
      </p>

      <h2>15. Release, Assumption of Risk, Indemnity, and Limitation of Liability</h2>
      <p>
        <strong>15.1 Assumption of Risk.</strong> You acknowledge that working with animals and
        advising animal owners involves inherent risks, including risks of injury, property damage,
        unpredictable behavior, client dissatisfaction, misunderstandings, and third-party claims.
        You knowingly assume all risks arising from the services you provide and from your
        interactions with Clients and animals.
      </p>
      <p>
        <strong>15.2 Release.</strong> To the fullest extent permitted by law, you release and
        discharge Pheefee and its officers, directors, employees, contractors, affiliates, agents,
        successors, and assigns from all claims, demands, causes of action, damages, liabilities,
        and losses arising out of or relating to: (a) services you provide or fail to provide; (b)
        disputes between you and any Client; (c) animal behavior, injury, property damage, or
        client conduct; (d) your reliance on any Client-provided information; or (e) your use of
        the platform, except to the extent caused by Pheefee&rsquo;s gross negligence, willful
        misconduct, or liability that cannot be waived under applicable law.
      </p>
      <p>
        <strong>15.3 Trainer Indemnity.</strong> You shall defend, indemnify, and hold harmless
        Pheefee and its officers, directors, employees, affiliates, contractors, agents,
        successors, and assigns from and against any and all claims, actions, demands,
        investigations, liabilities, damages, judgments, settlements, penalties, fines, costs, and
        expenses, including reasonable attorneys&rsquo; fees, arising out of or relating to:
      </p>
      <ul>
        <li>your services, acts, omissions, or misconduct;</li>
        <li>your breach of these Terms;</li>
        <li>your violation of law or third-party rights;</li>
        <li>
          bodily injury, animal injury, death, property damage, or other harm allegedly caused by
          you, your advice, or your conduct;
        </li>
        <li>any false, misleading, or infringing content or representation submitted by you; or</li>
        <li>any dispute between you and a Client.</li>
      </ul>
      <p>
        <strong>15.4 Disclaimer of Warranties.</strong> The platform is provided on an &ldquo;as
        is&rdquo; and &ldquo;as available&rdquo; basis. To the maximum extent permitted by law,
        Pheefee disclaims all warranties, express, implied, statutory, or otherwise, including
        warranties of merchantability, fitness for a particular purpose, title, non-infringement,
        uninterrupted availability, security, accuracy, or results.
      </p>
      <p>
        <strong>15.5 Limitation of Liability.</strong> To the fullest extent permitted by law,
        Pheefee shall not be liable for any indirect, incidental, special, consequential,
        exemplary, or punitive damages, or for any loss of profits, revenue, business opportunity,
        reputation, goodwill, data, or expected savings, arising out of or relating to these Terms
        or the platform, even if advised of the possibility of such damages.
      </p>
      <p>
        <strong>15.6 Liability Cap.</strong> To the fullest extent permitted by law,
        Pheefee&rsquo;s aggregate liability arising out of or relating to these Terms or the
        platform shall not exceed the total amount of fees actually retained by Pheefee from your
        use of the platform during the twelve (12) months immediately preceding the event giving
        rise to the claim.
      </p>
      <p>
        <strong>15.7 Non-Excludable Liability.</strong> Nothing in these Terms excludes liability
        that cannot be excluded or limited under applicable law.
      </p>

      <h2>16. Suspension, Removal, and Termination</h2>
      <p>
        <strong>16.1 Pheefee Rights.</strong> Pheefee may suspend, restrict, deactivate, or
        terminate your account or access to the platform immediately, with or without notice, if
        Pheefee determines, in its sole discretion, that:
      </p>
      <ul>
        <li>you breached these Terms or any platform policy;</li>
        <li>you received repeated complaints, poor feedback, or trust-and-safety reports;</li>
        <li>you failed a background check or later became ineligible;</li>
        <li>your insurance lapsed or became insufficient;</li>
        <li>you made false or misleading representations;</li>
        <li>
          you engaged in unprofessional, unlawful, unsafe, abusive, or platform-harmful conduct;
        </li>
        <li>you attempted to divert Pheefee-introduced Clients off-platform;</li>
        <li>you failed to cooperate with an investigation;</li>
        <li>you created legal, safety, payment, fraud, or reputational risk;</li>
        <li>
          your continued presence on the platform no longer serves Pheefee&rsquo;s business
          interests; or
        </li>
        <li>
          you make false, misleading, harassing, threatening, malicious, or
          confidentiality-breaching public statements about Pheefee, its personnel, Clients, or
          other trainers, or you use Pheefee&rsquo;s name, brand, or platform affiliation in a
          manner that falsely implies endorsement, partnership, sponsorship, or employment.
        </li>
      </ul>
      <p>
        <strong>16.2 Effect of Termination.</strong> Upon suspension or termination, Pheefee may
        remove your profile, cancel access, retain platform records, withhold or offset payouts as
        permitted by law and these Terms, and take any other reasonable steps to protect the
        platform and its users.
      </p>
      <p>
        <strong>16.3 Survival.</strong> Any provision that by its nature should survive suspension
        or termination shall survive, including provisions relating to payment adjustments,
        confidentiality, intellectual property, indemnity, release, limitations of liability,
        dispute resolution, and record retention.
      </p>

      <h2>17. Dispute Resolution</h2>
      <p>
        <strong>17.1 Governing Law.</strong> These Terms and any dispute arising out of or
        relating to them shall be governed by the laws of the State of Tennessee, without regard to
        conflict-of-law principles.
      </p>
      <p>
        <strong>17.2 Informal Resolution.</strong> Before filing any formal claim, the parties
        shall first attempt in good faith to resolve the dispute informally by written notice.
      </p>
      <p>
        <strong>17.3 Binding Arbitration.</strong> Except for claims eligible for small claims
        court or claims seeking temporary or preliminary injunctive relief, any dispute arising out
        of or relating to these Terms, the platform, or the relationship between the parties shall
        be resolved by final and binding arbitration administered by American Arbitration
        Association in accordance with its applicable rules.
      </p>
      <p>
        <strong>17.4 Venue.</strong> The arbitration shall take place in Davidson County,
        Tennessee, unless the parties agree otherwise or the applicable arbitration rules require a
        remote proceeding.
      </p>
      <p>
        <strong>17.5 Waiver of Jury Trial and Class Claims.</strong> To the fullest extent
        permitted by law, each party waives the right to trial by jury and the right to participate
        in any class, collective, coordinated, consolidated, or representative action or
        proceeding.
      </p>
      <p>
        <strong>17.6 Equitable Relief.</strong> Nothing in this Section prevents Pheefee from
        seeking injunctive or equitable relief in a court of competent jurisdiction for misuse of
        the platform, intellectual property infringement, unauthorized disclosure of confidential
        information, or threatened harm to the platform or its users.
      </p>
      <p>
        <strong>17.7 Arbitration Costs and Attorneys&rsquo; Fees.</strong> Each party shall bear
        its own attorneys&rsquo; fees and costs in any dispute arising out of or relating to these
        Terms, except to the extent the arbitrator awards otherwise under these Terms or applicable
        law. The parties shall share equally the arbitrator&rsquo;s fees and administrative costs
        of arbitration, except to the extent applicable law or the applicable arbitration rules
        require a different allocation, or the arbitrator reallocates such fees and costs in the
        final award. The prevailing party shall be entitled to recover its reasonable
        attorneys&rsquo; fees and costs to the extent permitted by applicable law and awarded by
        the arbitrator or court of competent jurisdiction.
      </p>

      <h2>18. General Terms</h2>
      <p>
        <strong>18.1 Changes to Terms.</strong> Pheefee may update these Terms from time to time.
        Updated Terms become effective upon posting or upon other notice given by Pheefee. Your
        continued use of the platform after the effective date of updated Terms constitutes
        acceptance.
      </p>
      <p>
        <strong>18.2 Electronic Consent.</strong> You agree that Pheefee may provide notices,
        disclosures, and other communications electronically and that your electronic acceptance of
        these Terms carries the same force as a handwritten signature.
      </p>
      <p>
        <strong>18.3 Assignment.</strong> You may not assign, transfer, delegate, or sublicense any
        rights or obligations under these Terms without Pheefee&rsquo;s prior written consent.
        Pheefee may assign these Terms in connection with a merger, sale, restructuring, or other
        corporate transaction.
      </p>
      <p>
        <strong>18.4 Severability.</strong> If any provision of these Terms proves unenforceable,
        the remaining provisions will remain in full force to the maximum extent permitted by law.
      </p>
      <p>
        <strong>18.5 Waiver.</strong> A failure or delay to enforce any provision of these Terms
        does not waive that provision or any other provision.
      </p>
      <p>
        <strong>18.6 Entire Agreement.</strong> These Terms, together with all documents and
        policies incorporated by reference, constitute the entire agreement between you and Pheefee
        regarding the subject matter hereof and supersede all prior or contemporaneous
        understandings on that subject.
      </p>
      <p>
        <strong>18.7 Notices.</strong> Legal notices to Pheefee must be sent to:
      </p>
      <p>
        <strong>Pheefee Technologies Inc.</strong>
        <br />
        <a href="mailto:pheefeeplatform@gmail.com">pheefeeplatform@gmail.com</a>
      </p>
      <p>
        Notices to you may be sent to the email address or other contact information associated
        with your account.
      </p>
    </LegalPageLayout>
  );
};

export default TrainerTermsAndConditions;
